import React from 'react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { useFragment, graphql } from 'react-relay';
import { EmptyFieldText } from '@atlassian/jira-issue-empty-field-text/src/ui/index.tsx';
import {
	useHoverPopoverContentRef,
	type HoverPopoverContentRef,
} from '@atlassian/jira-hover-popover/src/controllers/hover-popover-content-provider/index.tsx';
import messages from '@atlassian/jira-issue-empty-field-text/src/ui/messages.tsx';
import { useBespokeEmptyFieldTextGA } from '@atlassian/jira-issue-experiments/src/bespoke-empty-field-text-ga/index.tsx';
import type {
	cascadingSelect_issueFieldCascadingSelectReadviewFull_CascadingSelectReadView$key as CascadingSelectFragment,
	cascadingSelect_issueFieldCascadingSelectReadviewFull_CascadingSelectReadView$data as AggCascadingSelectTypes,
} from './__generated__/cascadingSelect_issueFieldCascadingSelectReadviewFull_CascadingSelectReadView.graphql';
import type { CascadingSelectReadViewProps } from './types.tsx';
import { CascadingSelectOptionTruncated } from './truncated/index.tsx';

const getFieldValueAsText = (cascadingOption: AggCascadingSelectTypes['cascadingOption']) => {
	if (!cascadingOption || !cascadingOption?.parentOptionValue) {
		return null;
	}

	if (!cascadingOption?.childOptionValue) {
		return cascadingOption.parentOptionValue.value;
	}

	return `${cascadingOption.parentOptionValue.value} - ${cascadingOption.childOptionValue.value}`;
};

/**
 * The CascadingSelectReadView will show a read only view of CascadingSelect field.
 * @param props [CascadingSelectReadViewProps](./types.tsx)
 */
export const CascadingSelectReadView = ({
	fragmentRef,
	isTruncated = false,
	shouldRenderHoverPopover,
}: CascadingSelectReadViewProps): React.JSX.Element => {
	const data = useFragment<CascadingSelectFragment>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment cascadingSelect_issueFieldCascadingSelectReadviewFull_CascadingSelectReadView on JiraCascadingSelectField {
				id
				cascadingOption {
					parentOptionValue {
						value
					}
					childOptionValue {
						value
					}
				}
			}
		`,
		fragmentRef,
	);

	const { cascadingOption } = data;

	const fieldValueAsText = getFieldValueAsText(cascadingOption);

	const hoverPopoverRefFactory = shouldRenderHoverPopover
		? // eslint-disable-next-line  react-hooks/rules-of-hooks
			useHoverPopoverContentRef(data.id)
		: null;

	let hoverPopoverRef: HoverPopoverContentRef = null;
	if (hoverPopoverRefFactory) {
		hoverPopoverRef = hoverPopoverRefFactory({
			content: fieldValueAsText,
		});
	}

	const isBespokeEmptyFieldTextGA = useBespokeEmptyFieldTextGA();
	if (!fieldValueAsText) {
		return (
			<EmptyFieldText
				{...(isBespokeEmptyFieldTextGA && {
					placeholderText: messages.bespokenCascadingSelectPlaceholderEmptyFieldText,
				})}
			/>
		);
	}

	if (isTruncated) {
		if (shouldRenderHoverPopover) {
			return <CascadingSelectOptionTruncated value={fieldValueAsText} ref={hoverPopoverRef} />;
		}
		return <CascadingSelectOptionTruncated value={fieldValueAsText} />;
	}
	return <>{fieldValueAsText}</>;
};
